import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Smartphone } from "lucide-react";
import "./audit-mobile.css";
import { AuditProvider, ROOMS, useAudit } from "./AuditContext";
import { capture } from "../../lib/analytics";
import { startAuditRun, enterRoom, exitRoom, hasOpenRoom } from "./auditAnalyticsTiming";
import { Stage } from "./Stage";
import { RehearsalRoom } from "./RehearsalRoom";
import { RehearsalRoomStructure } from "./RehearsalRoomStructure";
import { RehearsalRoomVoice } from "./RehearsalRoomVoice";
import { RehearsalRoomGesture } from "./RehearsalRoomGesture";
import { RehearsalRoomSpace } from "./RehearsalRoomSpace";
import { RehearsalRoomOpening } from "./RehearsalRoomOpening";
import { RehearsalRoomReflection } from "./RehearsalRoomReflection";
import { ResultsRoom } from "./ResultsRoom";
import { UITour } from "./UITour";
import { ROOM_CONFIGS } from "./rooms";
import { useIsMobileAudit } from "./useIsMobileAudit";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";

/**
 * The High-Stakes Presentation Audit — standalone immersive experience.
 * No site header, no menu, no footer. The room provides its own context
 * through scenic signage and the Presenter's dialogue.
 *
 * Navigation chrome — 4 fixed zones:
 *   • Bottom-right  ACTION      — CONTINUE (brand gold, pulses when ready)
 *   • Bottom-left   CORRECTION  — BACK + RESET ROOM (subtle outline)
 *   • Top-left      GUIDE       — REPLAY instructions (floating icon)
 *   • Top-right     SYSTEM      — EXIT → overlay menu (website / full restart)
 */

// Corner brand mark — top-right of the room, matching the mockup.
const ScenicLabel = () => (
  <div
    className="absolute top-0 right-0 z-30 flex flex-col items-end pt-5 md:pt-7 pr-5 md:pr-8 pointer-events-none"
    data-testid="scenic-label"
  >
    <p className="font-serif italic text-paper text-lg md:text-2xl leading-none tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
      InterAct Globally
    </p>
    <p className="font-hand text-mustard/95 text-base md:text-xl leading-tight mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]">
      the rehearsal room
    </p>
  </div>
);

// Full-screen mask shown only on mobile portrait — pure CSS media query
// (audit-mobile.css) shows/hides it, so rotating the device resumes the
// audit instantly with no JS listeners.
const OrientationMask = () => (
  <div
    id="audit-orientation-mask"
    data-testid="orientation-mask"
    className="fixed inset-0 z-[300] flex-col items-center justify-center gap-6 text-center px-10"
    style={{ background: "#141310" }}
  >
    <Smartphone
      size={58}
      className="text-mustard"
      style={{ animation: "audit-rotate-hint 1.8s ease-in-out infinite" }}
      aria-hidden="true"
    />
    <p className="font-serif text-paper text-xl leading-snug max-w-xs">
      For the best rehearsal experience, please rotate your device to landscape.
    </p>
  </div>
);

// Stage Manager's chart — 7 thin gold bars, centered top. Completed rooms
// are clickable (BACK navigation); the current one pulses softly.
const ROOM_LABELS = {
  message: "Message", structure: "Structure", voice: "Voice",
  gesture: "Gesture", space: "Space", opening: "Opening", reflection: "Reflection",
};

const StageProgress = () => {
  const { currentStep, goToStep } = useAudit();
  const rooms = ROOMS.filter((r) => r.id !== "performanceMap");
  const [labelVisible, setLabelVisible] = useState(true);

  // Show the room name for 3s on every room change, then fade it out.
  useEffect(() => {
    setLabelVisible(true);
    const t = setTimeout(() => setLabelVisible(false), 3000);
    return () => clearTimeout(t);
  }, [currentStep]);

  const label = ROOM_LABELS[ROOMS[currentStep]?.id];
  const roomNo = String(currentStep + 1).padStart(2, "0");

  return (
    <div
      className="absolute top-4 left-1/2 -translate-x-1/2 z-[95] flex flex-col items-center pointer-events-none"
      data-testid="stage-progress"
    >
      <div className="flex items-center gap-1.5 pointer-events-auto">
        {rooms.map((room, i) => {
          const done = i < currentStep;
          const current = i === currentStep;
          return (
            <button
              key={room.id}
              type="button"
              disabled={!done}
              onClick={() => done && goToStep(i)}
              title={done ? `Back to ${ROOM_LABELS[room.id]}` : undefined}
              aria-label={done ? `Back to ${ROOM_LABELS[room.id]}` : ROOM_LABELS[room.id]}
              data-testid={`progress-dot-${room.id}`}
              className={`flex items-center justify-center bg-transparent border-none p-0 ${done ? "cursor-pointer group" : "cursor-default"}`}
              style={{ width: 26, height: 18 }}
            >
              <motion.span
                aria-hidden="true"
                animate={current
                  ? { opacity: [0.5, 1, 0.5], scaleY: [1, 1.7, 1] }
                  : { opacity: 1, scaleY: 1 }}
                transition={current
                  ? { duration: 1.8, repeat: Infinity, ease: "easeInOut" }
                  : { duration: 0.3 }}
                className="block transition-transform duration-200 group-hover:scale-y-[1.7]"
                style={{
                  width: 20, height: 3, borderRadius: 2,
                  background: done || current ? "#da983c" : "rgba(255,255,255,0.28)",
                  boxShadow: current ? "0 0 8px rgba(218,152,60,0.8)" : "none",
                }}
              />
            </button>
          );
        })}
      </div>
      <AnimatePresence>
        {label && labelVisible && (
          <motion.p
            key={`room-label-${currentStep}`}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.8 } }}
            transition={{ duration: 0.5 }}
            className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-paper/85 drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]"
            data-testid="progress-room-label"
          >
            Room {roomNo}: {label}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

const NavBar = () => {
  const { currentStep, next, back, reset, resetRoom, canProceed, setRoomAnswer, currentRoom } = useAudit();
  const navigate = useNavigate();
  const isMobile = useIsMobileAudit();
  const [menuOpen, setMenuOpen] = useState(false);
  const [correctionMenuOpen, setCorrectionMenuOpen] = useState(false);
  const isFirst = currentStep === 0;
  const isLast = currentStep === ROOMS.length - 1;

  const btnGhost =
    "font-mono text-[10px] uppercase tracking-[0.18em] px-4 border transition-colors bg-black/30 backdrop-blur-sm border-paper/30 text-paper/85 hover:border-paper hover:text-paper disabled:opacity-30 disabled:cursor-not-allowed";

  // Replays the Presenter's instructions for the current room by rewinding
  // its intro dialogue. Task answers are untouched.
  const replayInstructions = () => setRoomAnswer(currentRoom.id, { introStep: 0 });

  return (
    <>
      {/* GUIDE ZONE — replay icon, floats near the speech-bubble area */}
      <button
        type="button"
        onClick={replayInstructions}
        title="Replay instructions"
        aria-label="Replay instructions"
        data-testid="audit-replay"
        className="absolute top-5 left-5 z-[95] flex items-center justify-center rounded-full border border-paper/35 bg-black/35 text-paper/85 backdrop-blur-sm hover:border-mustard hover:text-mustard transition-colors"
        style={{ width: 44, height: 44 }}
      >
        <RotateCcw size={18} />
      </button>

      {/* SYSTEM ZONE — minimal exit, below the corner brand mark */}
      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        data-testid="audit-exit"
        className="absolute right-6 z-[95] font-mono text-[10px] uppercase tracking-[0.2em] px-4 border border-paper/30 bg-black/30 text-paper/80 backdrop-blur-sm hover:border-paper hover:text-paper transition-colors"
        style={{ top: 84, minHeight: 44 }}
      >
        Exit
      </button>

      {/* Exit / Restart overlay menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="exit-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 z-[130]"
            style={{ background: "rgba(8,6,3,0.55)" }}
            onClick={() => setMenuOpen(false)}
            data-testid="audit-exit-menu"
          >
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className="absolute right-6 w-72 bg-[#1B1B18] border border-paper/20 shadow-[0_24px_60px_rgba(0,0,0,0.6)] p-2"
              style={{ top: 136 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => { exitRoom("explicit_exit"); navigate("/insights"); }}
                data-testid="audit-exit-website"
                className="w-full text-left font-mono text-[11px] uppercase tracking-[0.2em] px-4 text-paper/85 hover:bg-paper/10 hover:text-paper transition-colors"
                style={{ minHeight: 48 }}
              >
                Exit to Website
              </button>
              <button
                type="button"
                onClick={() => {
                  exitRoom("explicit_exit");
                  startAuditRun(window.location.pathname);
                  reset();
                  setMenuOpen(false);
                }}
                data-testid="audit-restart-full"
                className="w-full text-left font-mono text-[11px] uppercase tracking-[0.2em] px-4 text-terracotta hover:bg-paper/10 transition-colors"
                style={{ minHeight: 48 }}
              >
                Restart Full Audit
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CORRECTION ZONE — bottom-left. Desktop/tablet: two separate buttons.
          Mobile: collapsed into a single "···" dropdown to save space
          (see audit-mobile.css for the display split). The container itself
          is pointer-events:none — only the actual button(s) inside opt back
          in — so its padding/gap area never steals taps meant for content
          behind it (e.g. Room 05's grid cells, which sit low enough on a
          short mobile landscape screen to reach into this corner). */}
      <div
        className="absolute bottom-0 left-0 z-[95] flex items-center gap-3 p-5 md:p-8"
        style={{ pointerEvents: "none" }}
        data-testid="audit-correction-zone"
      >
        <button type="button" onClick={back} disabled={isFirst} data-testid="audit-back" className={`${btnGhost} audit-correction-desktop-btn`} style={{ minHeight: 44, pointerEvents: "auto" }}>
          ← Back
        </button>
        <button type="button" onClick={resetRoom} data-testid="audit-reset-room" className={`${btnGhost} audit-correction-desktop-btn`} style={{ minHeight: 44, pointerEvents: "auto" }}>
          Reset Room
        </button>
 
        {/* Mobile-only collapsed menu */}
        <div className="audit-correction-mobile-wrap" style={{ position: "relative", display: "none", pointerEvents: "auto" }}>
          <button
            type="button"
            onClick={() => setCorrectionMenuOpen((v) => !v)}
            data-testid="audit-correction-menu-btn"
            className={btnGhost}
            style={{ minHeight: 38, paddingTop: 6, paddingBottom: 6 }}
            aria-label="Room controls"
          >
            ⋯
          </button>
          <AnimatePresence>
            {correctionMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 6, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.96 }}
                transition={{ duration: 0.2 }}
                className="absolute bottom-full left-0 mb-2 bg-[#1B1B18] border border-paper/20 shadow-[0_16px_40px_rgba(0,0,0,0.55)] p-1.5 flex flex-col gap-1"
                style={{ width: 148 }}
                data-testid="audit-correction-menu"
              >
                <button
                  type="button"
                  onClick={() => { back(); setCorrectionMenuOpen(false); }}
                  disabled={isFirst}
                  data-testid="audit-back-mobile"
                  className="w-full text-left font-mono text-[10px] uppercase tracking-[0.18em] px-3 text-paper/85 hover:bg-paper/10 hover:text-paper transition-colors disabled:opacity-30"
                  style={{ minHeight: 38 }}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={() => { resetRoom(); setCorrectionMenuOpen(false); }}
                  data-testid="audit-reset-room-mobile"
                  className="w-full text-left font-mono text-[10px] uppercase tracking-[0.18em] px-3 text-paper/85 hover:bg-paper/10 hover:text-paper transition-colors"
                  style={{ minHeight: 38 }}
                >
                  Reset Room
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* GLOBAL mobile "tap anywhere to continue" — once a room's task is
          fully done (`canProceed`), tapping anywhere on screen advances to
          the next room, not just the small Continue button. Sits at a
          z-index below every room's own bubble-advance catcher (88) and
          speech bubble (100) — so those still take priority and this only
          catches taps that land on empty/background space or already-
          completed content, never double-firing `next()` alongside a
          room's own advance flow. `next()` itself already runs each
          room's `beforeNext` interceptor first (e.g. closing walk-off
          animations), so calling it directly here is exactly as safe as
          the Continue button.
          NOTE for future maintainers: at least one room (Room 01's
          arrival beat) uses a lower z:80 for its own catcher. That's only
          safe because that catcher exists exclusively while
          `canProceed===false` (this button isn't even rendered then) — if
          you ever set `canProceed:true` during an arrival/intro beat,
          double-check its catcher's z-index is still above 82. */}
      {isMobile && !isLast && canProceed && (
        <button
          type="button"
          onClick={next}
          aria-label="Continue to next room"
          data-testid="audit-global-tap-continue"
          className="absolute inset-0 w-full h-full cursor-pointer"
          style={{ zIndex: 82, background: "transparent", border: "none" }}
        />
      )}

      {/* ACTION ZONE — bottom-right. Same pointer-events pass-through as the
          correction zone: the container's own padding sat over grid cell 9
          on Room 05's short mobile landscape viewport, silently stealing
          taps meant for that cell. */}
      <div
        className="absolute bottom-0 right-0 z-[95] p-5 md:p-8"
        style={{ pointerEvents: "none" }}
        data-testid="audit-action-zone"
      >
        {!isLast ? (
          <motion.button
            type="button"
            onClick={next}
            disabled={!canProceed}
            data-testid="audit-next"
            animate={canProceed ? { scale: [1, 1.045, 1] } : { scale: 1 }}
            transition={canProceed
              ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
              : { duration: 0.2 }}
            className="px-7 font-mono text-[11px] uppercase tracking-[0.2em] text-ink shadow-lg hover:brightness-110 transition-[filter] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#da983c", minHeight: 46, pointerEvents: canProceed ? "auto" : "none" }}
          >
            Continue →
          </motion.button>
        ) : (
          <span className="font-hand text-mustard text-2xl" style={{ pointerEvents: "auto" }}>journey complete</span>
        )}
      </div>
    </>
  );
};

const AuditInner = () => {
  const { currentStep, start, overrideMessage, roomEpoch } = useAudit();
  const roomId = ROOMS[currentStep].id;
  const cfg = ROOM_CONFIGS[roomId];
  const Panel = cfg?.Panel;
  const BoardOverlay = cfg?.BoardOverlay;
  const activeMessage = overrideMessage || cfg?.message || "";

  useEffect(() => { start(); }, [start]);

  // Fires exactly once per genuine mount — "Restart Full Audit" (NavBar)
  // explicitly starts its own fresh run instead of relying on this.
  const auditStartedRef = useRef(false);
  useEffect(() => {
    if (auditStartedRef.current) return;
    auditStartedRef.current = true;
    startAuditRun(window.location.pathname);
  }, []);

  // Room enter/exit + active-duration timing — behavioral only, a room
  // being reached/left is never a confirmed business fact on its own.
  const prevRoomRef = useRef(null);
  useEffect(() => {
    const prev = prevRoomRef.current;
    if (prev && prev.roomId !== roomId) {
      exitRoom(currentStep > prev.currentStep ? "next_room" : "previous_room");
    }
    enterRoom(roomId, currentStep + 1);
    prevRoomRef.current = { roomId, currentStep };
    capture("audit_room_entered", { room_id: roomId, room_number: currentStep + 1 });
  }, [roomId, currentStep]);

  // Catch-all for any unmount not already explicitly handled (e.g.
  // browser back button, programmatic route change) — "page_exit" per
  // the room/page-exit taxonomy; the EXIT/Restart buttons already call
  // exitRoom('explicit_exit') themselves before unmounting, so this
  // fallback only ever fires for the OTHER, non-button-driven exits.
  useEffect(() => {
    return () => { if (hasOpenRoom()) exitRoom("page_exit"); };
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Rooms 01/02/03 use dedicated camera-free layouts that live directly
  // on the rehearsal-room scene. All other rooms continue to use Stage.
  const isRehearsalRoom = roomId === "message";
  const isStructureRoom = roomId === "structure";
  const isVoiceRoom     = roomId === "voice";
  const isGestureRoom   = roomId === "gesture";
  const isSpaceRoom     = roomId === "space";
  const isOpeningRoom   = roomId === "opening";
  const isReflectionRoom = roomId === "reflection";
  const isResultsRoom   = roomId === "performanceMap";
  const isCustomScene   = isRehearsalRoom || isStructureRoom || isVoiceRoom || isGestureRoom || isSpaceRoom || isOpeningRoom || isReflectionRoom || isResultsRoom;

  // Keying by roomEpoch lets "Reset Room" remount the room with fresh local state.
  const k = (id) => `${id}:${roomEpoch}`;

  return (
    <div className="fixed inset-0 z-[100] bg-black text-paper" data-testid="audit-page">
      {isCustomScene ? (
        <>
          {isRehearsalRoom && <RehearsalRoom key={k("message")} />}
          {isStructureRoom && <RehearsalRoomStructure key={k("structure")} />}
          {isVoiceRoom     && <RehearsalRoomVoice key={k("voice")} />}
          {isGestureRoom   && <RehearsalRoomGesture key={k("gesture")} />}
          {isSpaceRoom     && <RehearsalRoomSpace key={k("space")} />}
          {isOpeningRoom   && <RehearsalRoomOpening key={k("opening")} />}
          {isReflectionRoom && <RehearsalRoomReflection key={k("reflection")} />}
          {isResultsRoom   && <ResultsRoom key={k("performanceMap")} />}
          <NavBar />
        </>
      ) : (
        <Stage
          camera={cfg?.camera || "wide"}
          pose={cfg?.pose || "neutral"}
          message={activeMessage}
          background={roomId === "reflection" ? "/audit/bg_reflection.webp" : undefined}
          boardOverlay={BoardOverlay ? <BoardOverlay /> : null}
        >
          <ScenicLabel />
          <div data-testid={`audit-room-${roomId}`} className="relative w-full h-full">
            {Panel ? <Panel /> : null}
          </div>
          <NavBar />
        </Stage>
      )}
      <StageProgress />
      <UITour />
      <OrientationMask />
    </div>
  );
};

// Fullscreen entry gate — when the Audit is opened from the marketing site's
// "Start Free High-Stakes Audit" button, it launches in a NEW TAB with
// `?fs=1` on the URL. The Fullscreen API can only be requested from a real
// user gesture INSIDE that new tab (browsers block it on page load), so we
// show one small "tap to begin" screen whose click both requests fullscreen
// and dismisses itself. Unsupported browsers (e.g. iOS Safari, which has no
// Fullscreen API for arbitrary elements) simply proceed without it — the
// Audit still opens in its own tab either way.
const FullscreenGate = ({ children }) => {
  const wantsFullscreen = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("fs") === "1";
  const [gateOpen, setGateOpen] = useState(wantsFullscreen);

  const enter = () => {
    const el = document.documentElement;
    const request = el.requestFullscreen || el.webkitRequestFullscreen;
    try {
      request?.call(el)?.catch?.(() => {});
    } catch (e) { /* Fullscreen unsupported — proceed anyway. */ }
    setGateOpen(false);
  };

  if (!gateOpen) return children;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-6 text-center px-10 cursor-pointer"
      style={{ background: "#0c0a08" }}
      onClick={enter}
      data-testid="audit-fullscreen-gate"
    >
      <p className="font-serif italic text-paper text-2xl">InterAct Globally</p>
      <button
        type="button"
        onClick={enter}
        data-testid="audit-fullscreen-gate-btn"
        className="font-mono uppercase tracking-[0.24em] text-sm px-10 py-5 bg-terracotta text-paper hover:bg-ink transition-colors shadow-[0_16px_40px_rgba(0,0,0,0.45)]"
      >
        Tap to begin →
      </button>
    </div>
  );
};

export default function HighStakesAudit() {
  return (
    <>
      <PageMeta
        titleKey="meta.audit_title"
        descriptionKey="meta.audit_description"
        path={ROUTES.highStakesAudit}
        noindex
      />
      <AuditProvider>
        <FullscreenGate>
          <AuditInner />
        </FullscreenGate>
      </AuditProvider>
    </>
  );
}
